// 치과 「장착 후 확인」(환자 피드백) — Cloud Functions 호출 모음 (2026-10-02 대표 승인, 플래그 patientFeedback OFF 출고)
// 서버 계약: docs/계약_장착후확인_서버_2026-10-02.md §B-5~B-8. 계약이 바뀌면 이 파일 한 곳만 고친다.
// 화면 컴포넌트는 CF 이름·입출력 형태를 직접 알지 않는다. 내보내기·인쇄·복사·메모로 옮기기 경로는 없다(법무 L2).
// 공통: region asia-northeast3, onCall. 플래그 OFF면 failed-precondition {code:'feature-off'}.
// ⚠️ 콘솔에는 오류 코드만 남긴다 — 환자명·답 내용·자유 서술은 절대 로그에 쓰지 않는다.
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { isAppCheckError, APP_CHECK_REQUIRED_TEXT } from '../utils/appCheckError';

const REGION = 'asia-northeast3';
const call = (name) => httpsCallable(getFunctions(undefined, REGION), name);

export const FEEDBACK_GENERIC_ERROR = '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';

// 의뢰서 카드 고정 고지 — 서버 CLINIC_CARD_NOTICE 와 같은 문구(서버 응답 notice 가 우선, 이것은 응답에 없을 때만)
export const CLINIC_CARD_NOTICE = '환자가 보낸 전달본입니다. 진료기록이 아니며 필요한 사항은 진료기록부에 직접 기록해 주세요. 확인 후 7일이 지나면 삭제됩니다.';

// 시점 — 장착(도착 처리 다음 날) 기준 3일·14일·30일·180일 (계약 §C)
export const POINTS = Object.freeze(['D3', 'D14', 'D30', 'D180']);
export const POINT_LABEL = Object.freeze({ D3: '3일', D14: '2주', D30: '1개월', D180: '6개월' });
export const pointLabel = (p) => POINT_LABEL[p] || p || '-';
// 설정 기본값 — 계약 §A-1 "키 없음" 값 그대로
export const DEFAULT_FEEDBACK_SETTINGS = Object.freeze({ enabled: false, points: Object.freeze({ D3: true, D14: true, D30: false, D180: false }), units: Object.freeze({ other: false }) });

// 종료 사유(계약 §A-1 expiredReason)
export const EXPIRED_REASON_LABEL = Object.freeze({
    not_fitted: '환자가 "아직 장착 전"이라고 답함',
    no_response: '7일간 응답 없음',
    not_delivered: '전달 안 됨',
    consent_off: '환자가 질문 받기를 끔',
    cancelled: '의뢰서 연결 해제로 중단',
    recall_overlap: '재내원 안내와 겹쳐 생략',
    cycle_cap: '발송 횟수 상한(3회)으로 생략',
    held_expired: '발송 보류 중 기한 지나 종료',
});

// 마지막 시도 결과(계약 §A-1 lastResult) — 치과가 "왜 안 갔는지" 보는 용도
export const RESULT_LABEL = Object.freeze({
    ok: '발송됨', consent_off: '환자 미동의', consent_not_ready: '동의 문안 준비 중', consent_expired: '동의 2년 경과(재확인 필요)',
    notify_off: '환자가 앱 알림 끔', inactive: '환자 기록 비활성', unlinked: '의뢰서 연결 해제', clinic_off: '치과 설정에서 꺼짐',
    clinic_blocked: '치과 승인 상태 확인 필요', delegation_required: '개정 약관 재확인 필요', no_device: '앱 미설치',
    no_sender: '치과 이름(업체명) 미등록', banned: '치과명 문구 검사 보류(관리자 문의)', rate_limited: '일 상한 초과(다음 날 발송)',
    clinic_unpaid: '구독 확인 후 발송 재개', clinic_rejected: '치과 가입 승인 필요(발송 보류)', send_gate_unavailable: '발송 보류(확인 중 — 다음 날 다시 시도)',
    recall_overlap: '재내원 안내와 겹침', cycle_cap: '발송 횟수 상한', failed: '발송 실패', error: '오류',
});
export const resultLabel = (code) => (code ? (RESULT_LABEL[code] || code) : '');

// 시점 상태 → 표시(점 색·라벨). 빨강 = 확인 필요(불편 답 + 미확인), 초록 = 회신(불편 답 없음), 노랑 = 예정·회신 대기, 회색 = 확인함·종료·삭제
export const STATE_COLOR = Object.freeze({
    red: { dot: '#dc2626', bg: '#fee2e2', fg: '#b91c1c' },
    green: { dot: '#16a34a', bg: '#dcfce7', fg: '#15803d' },
    yellow: { dot: '#eab308', bg: '#fef9c3', fg: '#a16207' },
    gray: { dot: '#94a3b8', bg: '#f1f5f9', fg: '#64748b' },
});
export function pointState(row) {
    if (!row) return { key: 'none', label: '-', color: 'gray' };
    switch (row.status) {
        case 'answered': return row.flagged ? { key: 'need_check', label: '확인 필요', color: 'red' } : { key: 'answered', label: '회신 · 확인 전', color: 'green' };
        case 'acknowledged': return { key: 'acknowledged', label: '확인함', color: 'gray' };
        case 'pending': return { key: 'pending', label: '예정', color: 'yellow' };
        case 'sent': return { key: 'sent', label: '보냄 · 회신 대기', color: 'yellow' };
        case 'deleted': return { key: 'deleted', label: '전달본 삭제됨', color: 'gray' };
        case 'expired': return { key: 'expired', label: row.expiredReason ? `종료 · ${EXPIRED_REASON_LABEL[row.expiredReason] || row.expiredReason}` : '종료', color: 'gray' };
        default: return { key: row.status || 'none', label: row.status || '-', color: 'gray' };
    }
}

// 항목 칩 표시 — 서버 itemChip {itemId, unit, unitLabel, teeth[]} (재료·브랜드 없음, 법무 L3). 예: "46 임플란트" · "11-13 브릿지"
export function itemLabel(it) {
    if (!it) return '';
    const teeth = Array.isArray(it.teeth) ? it.teeth : [];
    const unit = it.unitLabel || '기타';
    if (teeth.length === 0) return unit;
    if (it.unit === 'bridge' && teeth.length > 1) return `${teeth[0]}-${teeth[teeth.length - 1]} ${unit}`;
    return `${teeth.join('·')} ${unit}`;
}

// 답 값(0·1·2) → 문항 선택지 라벨(서버 options 그대로). 없으면 '-'
export function answerLabel(q) {
    if (!q || q.answer === null || q.answer === undefined) return '답 없음';
    const o = (q.options || []).find(x => x.value === q.answer);
    return o ? o.label : String(q.answer);
}

// ms → YYYY.MM.DD (HH:mm)
export const fmtMs = (ms, withTime = false) => {
    if (!ms) return '';
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return '';
    const base = `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    return withTime ? `${base} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : base;
};

// 확인 필요 시점의 해당 항목 라벨 — "불편/네"(2) 답의 부위(sites) → 항목 칩. 부위가 없으면 [] (환자 상세 요약 줄용)
export function flaggedItemLabels(point) {
    if (!point) return [];
    const items = Array.isArray(point.items) ? point.items : [];
    const byId = new Map(items.map(i => [i.itemId, i]));
    const flaggedQids = new Set((point.questions || []).filter(q => q.answer === 2).map(q => q.qid));
    const ids = [...new Set((point.sites || []).filter(s => flaggedQids.has(s.qid)).map(s => s.itemId))];
    return ids.map(id => byId.get(id)).filter(Boolean).map(itemLabel);
}

// 시점 1줄 요약 — "3일 확인 필요(46 임플란트)" / "2주 예정"
export function pointSummary(point) {
    const st = pointState(point);
    if (st.key === 'need_check') {
        const its = flaggedItemLabels(point);
        return { text: `${pointLabel(point.point)} 확인 필요${its.length ? `(${its.join(', ')})` : ''}`, color: st.color };
    }
    const short = st.key === 'expired' ? '종료' : st.label;
    return { text: `${pointLabel(point.point)} ${short}`, color: st.color };
}

// CF 오류 → 화면 문구. 원본은 코드만 console.error (조용한 실패 금지 · 환자 정보 로그 금지)
export function describeFeedbackError(e, fallback = FEEDBACK_GENERIC_ERROR) {
    console.error('[clinicFeedbackApi]', e?.code || '', e?.details?.code || '');
    if (isAppCheckError(e)) return APP_CHECK_REQUIRED_TEXT;
    const c = e?.details?.code;
    if (e?.code === 'functions/unauthenticated') return '로그인이 필요합니다.';
    if (e?.code === 'functions/permission-denied') return e?.message || '권한이 없습니다.';
    if (e?.code === 'functions/resource-exhausted') return '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.';
    if (e?.code === 'functions/not-found') return '회신을 찾을 수 없습니다. 화면을 새로고침해 주세요.';
    if (e?.code === 'functions/failed-precondition') {
        if (c === 'feature-off') return '장착 후 확인 기능이 아직 열리지 않았습니다.';
        // 계약 B-8: 약관 v7.0(환자정보 처리위탁 사목) 재확인 전에는 켜기가 거부된다 — 재확인 UI 전까지 모든 치과가 이 코드(의도된 잠금)
        if (c === 'delegation-reconfirm-required') return '개정 약관(환자정보 처리위탁 항목) 재확인 후 켤 수 있습니다. 약관 재확인 배너에서 동의해 주세요.';
        if (typeof c === 'string' && c.startsWith('status:')) return '이미 처리되었거나 확인할 수 없는 상태입니다. 화면을 새로고침해 주세요.';
        return e?.message || fallback;
    }
    if (e?.code === 'functions/invalid-argument') return '요청 값이 올바르지 않습니다. 화면을 새로고침해 주세요.';
    return fallback;
}

// 계약 B-5 — bucket ∈ need_check|answered|pending|expired. { ok, bucket, count, truncated, items:[row] } (답 값 없음)
export const FEEDBACK_BUCKETS = Object.freeze(['need_check', 'answered', 'pending', 'expired']);
export async function listClinicFeedbacks(bucket) {
    const res = await call('listClinicFeedbacks')({ bucket });
    return res.data || { ok: false, items: [], count: 0, truncated: false };
}

// 계약 B-6 — { ok, orderId, linked, pid?, notice, points:[{…행, fitted, questions:[{…, answer}], sites, freeText}] } (D3→D180 순)
export async function getOrderFeedbacks(orderId) {
    const res = await call('getOrderFeedbacks')({ orderId });
    return res.data || { ok: false, linked: false, points: [] };
}

// 계약 B-7 — answered → acknowledged. { ok, already, status, deleteAt }
export async function acknowledgeFeedback(fbId) {
    const res = await call('acknowledgeFeedback')({ fbId });
    return res.data || { ok: false };
}

// 계약 B-8 — 치과 대표만. { ok, settings:{enabled, points, units} }. 자유 문구 입력 없음
export async function setClinicFeedbackPrefs({ enabled, points, units }) {
    const payload = { enabled: !!enabled };
    if (points) payload.points = POINTS.reduce((acc, p) => { acc[p] = !!points[p]; return acc; }, {});
    // 서버는 units 키를 { other:boolean } 만 허용(그 외 키 = invalid-argument) — other 만 보낸다
    if (units && typeof units.other === 'boolean') payload.units = { other: units.other };
    const res = await call('setClinicFeedbackPrefs')(payload);
    return res.data || { ok: false };
}

// 저장된 설정 직접 읽기 — clinicPatientRecords/{clinicUid}.feedbackSettings (rules: get = 치과 오너·직원, 계약 §A-1). 없으면 기본값
export function normalizeFeedbackSettings(s) {
    const src = s && typeof s === 'object' ? s : {};
    const points = {};
    POINTS.forEach(p => { points[p] = src.points && typeof src.points[p] === 'boolean' ? src.points[p] : DEFAULT_FEEDBACK_SETTINGS.points[p]; });
    return { enabled: src.enabled === true, points, units: { other: !!(src.units && src.units.other === true) } };
}
export async function readClinicFeedbackSettings(clinicUid) {
    const snap = await getDoc(doc(db, 'clinicPatientRecords', clinicUid));
    return normalizeFeedbackSettings(snap.exists() ? snap.data()?.feedbackSettings : null);
}
